import React from 'react'
import { useTranslation } from 'react-i18next'
import { toZonedWallClock, zonedWallClockToIso } from '@/lib/utils/zonedDay'
import type { PostSchedule } from '@/lib/sdk-official/resources/scheduled-posts'

/*
 * F-164: scheduling a post (content and status). The date is typed in the
 * zone of whoever schedules (the browser's), not the community's: "amanhã às
 * 9h" is the person's 9h. The API takes any ISO date after now and at most a
 * year ahead.
 */

/** The browser's zone, the one the field reads and shows. */
export const schedulerZone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
}

const YEAR_MS = 365 * 24 * 60 * 60 * 1000

/** The API's codes on `publishAt` (and `notScheduled`, `useReschedule` on an edit). */
export const SCHEDULE_ERROR_CODES = ['past', 'tooFar', 'invalid', 'useReschedule', 'notScheduled', 'withPublishedAt'] as const

type ApiErrorShape = { response?: { data?: { message?: string; errors?: { param?: string; message?: string; code?: string }[] } } }

/**
 * The refusal of a schedule, translated by its code (`postSchedule.errors.*`),
 * else the API's own message; null when the error is about something else.
 */
export const useScheduleErrorOf = () => {
  const { t } = useTranslation('common')
  return React.useCallback((err: unknown): string | null => {
    const data = (err as ApiErrorShape)?.response?.data
    const item = data?.errors?.find((e) => e.param === 'publishAt' || (e.code && (SCHEDULE_ERROR_CODES as readonly string[]).includes(e.code)))
    if (!item) return null
    return item.code && (SCHEDULE_ERROR_CODES as readonly string[]).includes(item.code)
      ? t(`postSchedule.errors.${item.code}`)
      : item.message || data?.message || t('postSchedule.errors.invalid')
  }, [t])
}

/** The field's wall clock (`YYYY-MM-DDTHH:mm`, the scheduler's zone) as the request's `publishAt`, checked like the API. */
export const useSchedulePayload = () => {
  const { t } = useTranslation('common')
  return React.useCallback((wall: string): { value?: string; error?: string } => {
    if (!wall) return {}
    const iso = zonedWallClockToIso(wall, schedulerZone())
    if (!iso) return { error: t('postSchedule.errors.invalid') }
    const at = new Date(iso).getTime()
    if (at <= Date.now()) return { error: t('postSchedule.errors.past') }
    if (at > Date.now() + YEAR_MS) return { error: t('postSchedule.errors.tooFar') }
    return { value: iso }
  }, [t])
}

/** A stored `publishAt` as the field's wall clock (for rescheduling). */
export const scheduleWallClock = (iso?: string | null): string => toZonedWallClock(iso, schedulerZone())

/** "3 de nov., 10:00" in the reader's language and zone. */
export const formatScheduleDate = (iso: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso))

/* After a post is scheduled, the shell says so (the post is not in the feed yet). */
export interface ScheduledAnnouncement { publishAt: string; spaceId?: string | null }
const listeners = new Set<(a: ScheduledAnnouncement) => void>()
export const onScheduled = (listener: (a: ScheduledAnnouncement) => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}
/** Called by a composer with the answer's `schedule`; nothing when the post went out now. */
export const announceScheduled = (schedule: PostSchedule | null | undefined, spaceId?: string | null) => {
  if (schedule?.status !== 'scheduled') return
  listeners.forEach((listener) => listener({ publishAt: schedule.publishAt, spaceId }))
}

/** "Agendados" (F-164), optionally of one space. */
export const scheduledPath = (spaceId?: string | null) => (spaceId ? `/scheduled?spaceId=${spaceId}` : '/scheduled')
